import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, interval, takeUntil, fromEvent } from 'rxjs';
import { BillingStatusService } from '../../../core/services/billing-status.service';
import { StoreContextService } from '../../../core/services/store-context.service';
import { PermissionService } from '../../../core/services/permission.service';
import {
  BillingStatus, blankStatus, shouldShowNotice, showPayNow, canViewStatus,
  noticeCopy, NoticeCopy, PAYMENT_IN_PROGRESS, STOREFRONT_DOWN_LINE, isSettled,
  shouldKeepPolling, retryAfterSeconds, isPermissionDenied, payNowUrl, apiMessage,
  NO_PAY_PERMISSION, POLL_INTERVAL_MS,
  // v1.6.1570
  graceNote, isChargebeeOutage, chargebeeErrorCode, CHARGEBEE_RETRY_NOTE,
} from '../../../core/utils/billing-restriction.utils';

/**
 * v1.6.1538 — the billing notice (Asana 1218431462710845).
 *
 * Staff see the consequence of the owner's billing, never the invoices. Sits
 * above the app so a mid-session block paints from any screen.
 */
@Component({
  selector: 'app-billing-notice',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './billing-notice.component.html',
})
export class BillingNoticeComponent implements OnInit, OnDestroy {
  status: BillingStatus = blankStatus();
  paying = false;
  payError = '';
  /** Seconds left on a 429 cool-down; the button stays disabled until zero. */
  retryIn = 0;
  /** True from the moment Pay Now opens until the balance clears. */
  paymentInProgress = false;

  private destroy$ = new Subject<void>();
  private pollStartedAt = 0;

  constructor(
    private billing: BillingStatusService,
    private storeCtx: StoreContextService,
    public perms: PermissionService,
  ) {}

  ngOnInit(): void {
    this.billing.lastError$.pipe(takeUntil(this.destroy$)).subscribe(e => {
      // Only a genuine failure speaks; a missing permission does not.
      this.readError = e && e.kind === 'failed' ? e.message : null;
    });

    this.billing.status$.pipe(takeUntil(this.destroy$)).subscribe(s => {
      this.status = s;
      // A partial payment does not clear this — isSettled checks the balance.
      if (this.paymentInProgress && isSettled(s)) this.paymentInProgress = false;
    });

    if (canViewStatus(this.perms)) {
      /**
       * The gate is evaluated per request on the `X-Store-Id` sent, so a store
       * switch can change the verdict entirely — one unpaid store must not read
       * as a whole-panel lock-out, nor the reverse.
       */
      this.storeCtx.context$.pipe(takeUntil(this.destroy$))
        .subscribe(() => this.billing.fetch(this.storeCtx.snapshot?.id ?? null).subscribe());
    }

    // Re-check when the operator comes back from the Chargebee tab.
    fromEvent(document, 'visibilitychange').pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!document.hidden && this.paymentInProgress) this.refresh();
    });

    interval(1000).pipe(takeUntil(this.destroy$))
      .subscribe(() => { if (this.retryIn > 0) this.retryIn--; });
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  get visible(): boolean { return shouldShowNotice(this.status); }

  /**
   * v1.6.1572 — the status could not be read.
   *
   * The verdict degrades to ALLOWED so nothing locks, which means a past-due
   * tenant renders exactly like a healthy one. This is the only thing on screen
   * that distinguishes "checked, nothing owed" from "could not check".
   *
   * A 403 stays silent: that staff member simply does not see billing.
   */
  readError: string | null = null;
  get readErrorVisible(): boolean { return !this.visible && !!this.readError; }

  /**
   * v1.6.1570 — a balance IS owed and a grace exception is covering it.
   *
   * The state is `allowed`, so the warning banner stays down and nothing else
   * on this screen would mention it. The owner would find out when grace lapses
   * and the panel restricts.
   */
  get graceNote(): string | null { return graceNote(this.status); }
  get graceVisible(): boolean { return !this.visible && this.graceNote != null; }
  get canPay(): boolean { return showPayNow(this.status, this.perms) && this.retryIn === 0; }
  get storefrontLine(): string | null {
    return this.status.storefront_down ? STOREFRONT_DOWN_LINE : null;
  }

  get copy(): NoticeCopy {
    // While a payment is settling the state is still "restricted" — saying so
    // again would read as though the payment failed.
    return this.paymentInProgress
      ? PAYMENT_IN_PROGRESS
      : noticeCopy(this.status, this.storeCtx.snapshot?.name ?? null);
  }

  refresh(): void { this.billing.fetch(this.storeCtx.snapshot?.id ?? null).subscribe(); }

  /**
   * Opens the payment page in a NEW TAB.
   *
   * The tab is opened synchronously on the click and its location set when the
   * url arrives — opening it inside the response handler is what a popup
   * blocker stops (v1.6.1503).
   */
  payNow(): void {
    if (this.paying || !this.canPay) return;
    this.paying = true;
    this.payError = '';
    const tab = window.open('', '_blank');
    this.billing.payNow().subscribe({
      next: (res: any) => {
        this.paying = false;
        const url = payNowUrl(res);
        if (!url) { tab?.close(); this.payError = 'No payment page was returned.'; return; }
        if (tab) tab.location.href = url; else window.location.href = url;
        this.paymentInProgress = true;
        this.startPolling();
      },
      error: (e: any) => {
        this.paying = false;
        tab?.close();
        /**
         * A 403 with no `data.reason` is a missing permission, NOT a billing
         * block — keep the notice, hide the button, say who can pay.
         */
        if (isPermissionDenied(e)) { this.payError = NO_PAY_PERMISSION; return; }
        /**
         * v1.6.1570 — a Chargebee outage is explicitly SAFE TO RETRY and
         * nothing was charged, so it must not read like a refusal.
         */
        if (isChargebeeOutage(e)) {
          const code = chargebeeErrorCode(e);
          this.payError = `${apiMessage(e, 'The payment page could not be opened.')} `
            + `${CHARGEBEE_RETRY_NOTE}${code ? ` (${code})` : ''}`;
          return;
        }
        const wait = retryAfterSeconds(e);
        if (wait != null) { this.retryIn = wait; this.payError = apiMessage(e, 'Too many attempts.'); return; }
        this.payError = apiMessage(e, 'The payment page could not be opened.');
      },
    });
  }

  /**
   * Restoration is never instant — typically one to three minutes — so this
   * polls rather than asking the operator to refresh. It stops after five so a
   * forgotten tab does not hammer the endpoint.
   */
  private startPolling(): void {
    this.pollStartedAt = Date.now();
    interval(POLL_INTERVAL_MS).pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.paymentInProgress) return;
      if (!shouldKeepPolling(this.pollStartedAt, Date.now())) { this.paymentInProgress = false; return; }
      this.refresh();
    });
  }
}
